<template>
	<div id="app" :class="{ 'dark-theme': isDarkMode }">
		<nav>
			<!-- Klikalne logo prowadzące na stronę główną -->
			<router-link to="/">
				<img src="@/assets/logo.png" alt="d4quality logo" class="logo" />
			</router-link>
			<div class="nav-links" :class="{ 'nav-active': isMenuOpen }">
				<router-link to="/ai" @click="closeMenu">Sztuczna Inteligencja</router-link> |
				<router-link to="/consulting" @click="closeMenu">Konsulting AI</router-link> |
				<router-link to="/courses" @click="closeMenu">Szkolenia AI</router-link> |
				<router-link to="/english" @click="closeMenu">Język Angielski</router-link> |
				<router-link to="/contact" @click="closeMenu">Kontakt</router-link>
			</div>
			<!-- Przycisk motywu w nawigacji -->
			<button @click="toggleTheme" class="theme-toggle">
				<span v-if="isDarkMode">🌙</span>
				<span v-else>☀️</span>
				{{ isDarkMode ? ' Tryb jasny' : ' Tryb ciemny' }}
			</button>
			<!-- Ikona menu dla urządzeń mobilnych -->
			<button class="menu-toggle" @click="toggleMenu">
				<font-awesome-icon :icon="isMenuOpen ? 'times' : 'bars'" />
			</button>
		</nav>

		<router-view />

		<!-- Stopka z informacją RODO i cookies -->
		<footer class="footer">
			<div class="footer-content">
				<b>D4 Quality SP Z O O</b>
				<p>Providing top-notch IT solutions and consulting services.</p>
				<p>© {{ currentYear }} D4 Quality SP Z O O. All rights reserved.</p>
				<p class="rodo-text">
					Ta strona używa cookies, aby poprawić Twoje doświadczenie. </p>
					<p>Szczegóły znajdziesz w naszej
					<a href="/privacy-policy" class="footer-link">polityce prywatności</a>.
				</p>
				<p style="border-top: 1px solid red; padding-top: 10px;">
					<a href="/privacy-policy" class="footer-link">Polityka prywatności</a> |
					<a href="/contact" class="footer-link">Kontakt</a>
				</p>
				<!-- Widget ElevenLabs -->
				<elevenlabs-convai agent-id="aUYxs27WTWsfz3ehevvN"></elevenlabs-convai>
				<div class="social-links">
					<a href="https://www.facebook.com/profile.php?id=61559008005879" target="_blank"
						class="social-link">
						<font-awesome-icon icon="fa-brands fa-facebook-f" />
					</a>
					<a href="https://www.linkedin.com/company/90861663/admin/feed/posts/" target="_blank"
						class="social-link">
						<font-awesome-icon icon="fa-brands fa-linkedin-in" />
					</a>
					<a href="https://www.youtube.com/@D4Quality" target="_blank" class="social-link">
						<font-awesome-icon icon="fa-brands fa-youtube" />
					</a>
					<a href="https://www.instagram.com/d4quality" target="_blank" class="social-link">
						<font-awesome-icon icon="fa-brands fa-instagram" />
					</a>
					<a href="https://www.x.com/d4quality" target="_blank" class="social-link">
						<font-awesome-icon icon="fa-brands fa-x-twitter" />
					</a>
				</div>
			</div>
		</footer>

		<!-- Powiadomienie o cookies -->
		<div v-if="showCookieBanner" class="cookie-banner">
			<p>
				Używamy cookies, aby zapewnić najlepszą jakość usług. Klikając "Akceptuję", zgadzasz się na ich użycie
				zgodnie z naszą
				<a href="/privacy-policy" class="cookie-link">polityką prywatności</a>.
			</p>
			<button class="cookie-accept" @click="acceptCookies">Akceptuję</button>
		</div>
	</div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';

library.add(fab, faBars, faTimes);

export default {
	components: {
		FontAwesomeIcon,
	},
	data() {
		return {
			isDarkMode: localStorage.getItem('theme') === 'dark',
			isMenuOpen: false,
			showCookieBanner: !localStorage.getItem('cookiesAccepted'), // Pokazuj baner, jeśli cookies nie zaakceptowane
		};
	},
	provide() {
		return {
			theme: this,
		};
	},
	computed: {
		currentYear() {
			return new Date().getFullYear(); // Dynamiczny rok w stopce
		},
	},
	mounted() {
		if (!document.querySelector('script[src="https://elevenlabs.io/convai-widget/index.js"]')) {
			const script = document.createElement('script');
			script.src = 'https://elevenlabs.io/convai-widget/index.js';
			script.async = true;
			script.type = 'text/javascript';
			document.head.appendChild(script);
		}
	},
	methods: {
		toggleTheme() {
			const icon = this.$el.querySelector('.theme-toggle span');
			if (icon) {
				icon.classList.add('rotate-icon');
				setTimeout(() => icon.classList.remove('rotate-icon'), 500);
			}
			this.isDarkMode = !this.isDarkMode;
			localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
		},
		toggleMenu() {
			this.isMenuOpen = !this.isMenuOpen;
		},
		closeMenu() {
			this.isMenuOpen = false;
		},
		acceptCookies() {
			localStorage.setItem('cookiesAccepted', 'true'); // Zapisz akceptację w localStorage
			this.showCookieBanner = false; // Ukryj baner
		},
	},
};
</script>

<style>
#app {
	font-family: 'Arial', sans-serif;
	text-align: center;
	margin-top: 60px;
	position: relative;
	transition: all 0.3s ease;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
}

#app:not(.dark-theme) {
	background-color: #ffffff;
	color: #1a1a1a;
}

#app.dark-theme {
	background-color: #1a1a1a;
	color: #ffffff;
}

nav {
	padding: 15px 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	flex-wrap: wrap;
	position: fixed;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 100%;
	max-width: 1200px;
	z-index: 1000;
	background-color: inherit;
	transition: background-color 0.3s ease;
}

nav:not(.dark-theme) {
	background-color: #ffffff;
	border-bottom: 2px solid #ff0000;
}

nav.dark-theme {
	background-color: #000000;
	border-bottom: 2px solid #ff0000;
}

.logo {
	height: 40px;
	cursor: pointer;
	transition: transform 0.3s ease;
}

.logo:hover {
	transform: scale(1.1);
}

.nav-links {
	display: flex;
	gap: 15px;
	flex-grow: 1;
	justify-content: center;
}

nav a {
	font-weight: bold;
	text-decoration: none;
	transition: color 0.3s ease;
}

nav:not(.dark-theme) a {
	color: #1a1a1a;
}

nav:not(.dark-theme) a:hover {
	color: #00b4d8;
}

nav:not(.dark-theme) a.router-link-exact-active {
	color: #ff0000;
}

nav.dark-theme a {
	color: #ffffff;
}

nav.dark-theme a:hover {
	color: #00b4d8;
}

nav.dark-theme a.router-link-exact-active {
	color: #ff0000;
}

.theme-toggle {
	padding: 8px 12px;
	background-color: #ff0000;
	color: #ffffff;
	border: none;
	border-radius: 20px;
	cursor: pointer;
	font-size: 12px;
	display: flex;
	align-items: center;
	gap: 6px;
	transition: background-color 0.3s ease, transform 0.2s ease;
}

.theme-toggle:hover {
	background-color: #00b4d8;
	transform: scale(1.05);
}

.theme-toggle .rotate-icon {
	animation: rotate 0.5s ease;
}

@keyframes rotate {
	from {
		transform: rotate(0deg);
	}

	to {
		transform: rotate(360deg);
	}
}

/* Styl dla ikony menu */
.menu-toggle {
	display: none;
	background: none;
	border: none;
	cursor: pointer;
	font-size: 20px;
}

nav:not(.dark-theme) .menu-toggle {
	color: #1a1a1a;
}

nav.dark-theme .menu-toggle {
	color: #ffffff;
}

.footer {
	padding: 20px 0;
	background-color: #000000;
	color: #ffffff;
	text-align: center;
	margin-top: auto;
	border-top: 2px solid #ff0000;
	transition: background-color 0.3s ease, color 0.3s ease;
}

.footer:not(.dark-theme) {
	background-color: #ffffff;
	color: #1a1a1a;
	border-top: 2px solid #ff0000;
}

.footer-content {
	max-width: 600px;
	margin: 0 auto;
	padding: 0 20px;
}

.footer-content p {
	margin: 5px 0;
	font-size: 14px;
}

.footer-link {
	color: #00b4d8;
	text-decoration: none;
}

.footer-link:hover {
	color: #ff0000;
}

.rodo-text {
	margin-top: 10px;
}

.social-links {
	margin-top: 10px;
	display: flex;
	justify-content: center;
	gap: 15px;
}

.social-link {
	display: inline-flex;
	align-items: center;
	transition: color 0.3s ease, transform 0.3s ease;
}

.social-link:not(.dark-theme) {
	color: #1a1a1a;
}

.social-link:not(.dark-theme):hover {
	color: #00b4d8;
}

.social-link.dark-theme {
	color: #ffffff;
}

.social-link.dark-theme:hover {
	color: #00b4d8;
}

elevenlabs-convai {
	display: block;
	margin: 20px auto;
}

/* Powiadomienie o cookies */
.cookie-banner {
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	background: #f5f5f5;
	padding: 15px;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 20px;
	box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
	z-index: 1000;
}

.dark-theme .cookie-banner {
	background: #2d2d2d;
}

.cookie-banner p {
	margin: 0;
	font-size: 0.9rem;
	color: #6b7280;
}

.dark-theme .cookie-banner p {
	color: #e0e0e0;
}

.cookie-link {
	color: #00b4d8;
	text-decoration: none;
}

.cookie-link:hover {
	color: #ff0000;
}

.cookie-accept {
	background: #00b4d8;
	color: #fff;
	border: none;
	padding: 8px 16px;
	border-radius: 6px;
	cursor: pointer;
}

.cookie-accept:hover {
	background: #ff0000;
}

/* Responsywność */
@media (max-width: 768px) {
	nav {
		padding: 10px;
		max-width: 100%;
	}

	.logo {
		height: 30px;
	}

	.nav-links {
		display: none;
		position: absolute;
		top: 60px;
		left: 50%;
		transform: translateX(-50%);
		width: 100%;
		max-width: 100vw;
		background-color: inherit;
		flex-direction: column;
		gap: 10px;
		padding: 10px 20px;
		text-align: center;
		z-index: 999;
		box-sizing: border-box;
		overflow-x: hidden;
	}

	.nav-links.nav-active {
		display: flex;
	}

	nav a {
		display: block;
		padding: 10px 0;
		white-space: nowrap;
	}

	.theme-toggle {
		position: static;
	}

	.menu-toggle {
		display: block;
	}

	.footer-content {
		padding: 0 10px;
		max-width: 90%;
	}

	.social-links {
		flex-direction: column;
		gap: 10px;
	}

	.social-link {
		margin: 0;
	}

	.cookie-banner {
		flex-direction: column;
		gap: 10px;
		padding: 10px;
	}
}

@media (max-width: 480px) {
	h1 {
		font-size: 1.5rem;
	}

	.footer-content p {
		font-size: 12px;
	}

	.social-links {
		gap: 5px;
	}

	.theme-toggle {
		font-size: 10px;
		padding: 6px 10px;
	}

	.footer-content {
		max-width: 95%;
	}

	.nav-links {
		padding: 10px 15px;
	}
}
</style>